import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(undefined);

const THEME_KEY = 'backlinkguard-theme';
const ACCENT_KEY = 'backlinkguard-accent';

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(THEME_KEY) || 'system';
    }
    return 'system';
  });

  const [accentColor, setAccentColor] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(ACCENT_KEY) || 'emerald';
    }
    return 'emerald';
  });

  // Determine effective theme (resolving 'system' to actual theme)
  const getEffectiveTheme = () => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme;
  };

  const [effectiveTheme, setEffectiveTheme] = useState(getEffectiveTheme);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    const effective = getEffectiveTheme();
    setEffectiveTheme(effective);

    // Toggle light class (dark is default, so we only add .light for light mode)
    if (effective === 'light') {
      root.classList.add('light');
    } else {
      root.classList.remove('light');
    }

    // Persist theme preference
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  // Listen for system theme changes when in 'system' mode
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      const effective = getEffectiveTheme();
      setEffectiveTheme(effective);

      const root = document.documentElement;
      if (effective === 'light') {
        root.classList.add('light');
      } else {
        root.classList.remove('light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Apply accent color to document
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme-palette', accentColor);
    localStorage.setItem(ACCENT_KEY, accentColor);
  }, [accentColor]);

  const value = {
    theme,
    setTheme,
    accentColor,
    setAccentColor,
    effectiveTheme,
    isDark: effectiveTheme === 'dark',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
